<script lang="ts" context="module">
  export interface EventHeaderProps {
    event: EventEntry;
  }
</script>

<script lang="ts">
  import LayeredSection from "../layout/LayeredSection.svelte";
  import Duration from "./Duration.svelte";
  import type { SubsplashMediaItem } from "../../types/entries";
  import { isBackgroundPlayerPaused } from "../../stores/playerStore.ts";
  import PublishDate from "./PublishDate.svelte";
  import ShareMedia from "./ShareMedia.svelte";
  import Button from "@elements/Button.svelte";
  import Prose from "@elements/Prose.svelte";

  let video: HTMLVideoElement;
  $: video && ($isBackgroundPlayerPaused ? video.pause() : video.play());

  function getMediaItemType(mediaItem: SubsplashMediaItem) {
    if (mediaItem._embedded?.video) {
      return "Video";
    }
    if (mediaItem._embedded?.audio) {
      return "Audio";
    }
    if (mediaItem._embedded?.document) {
      return "Reading";
    }
    return "Media";
  }

  export let mediaItem: SubsplashMediaItem;
  export let hasSharingButton: boolean = true;
</script>

<LayeredSection
  bind:video
  addClasses="box-border flex items-center justify-start overflow-hidden bg-cover bg-center"
  layers={[
    {
      backgroundCSS:
        "background-blend-mode: normal, normal, color, color-dodge, normal; background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 65.62%, #010101 100%), linear-gradient(0deg, rgba(1, 1, 1, 0.10) 0%, rgba(1, 1, 1, 0.10) 100%);",
    },
  ]}
>
  <div
    class="@container pt-48 pb-24 container w-full z-10 text-white gap-6 md:gap-x-24 grid grid-cols-1 md:grid-cols-2"
  >
    <div
      class="row-start-2 row-span-1 col-start-1 col-span-2 md:col-span-1 md:row-start-1 md:row-span-2"
    >
      {#if mediaItem?.series?.title && mediaItem?.series?.uri}
        <a
          href={mediaItem.series.uri}
          class="text-p100 mb-4 text-h6 cursor-pointer"
        >
          {mediaItem.series.title}
        </a>
      {/if}
      {#if mediaItem.title}
        <h1
          class="text-white text-h1 normal-case font-bold mb-4"
          data-pagefind-filter={`type:${getMediaItemType(mediaItem)}`}
        >
          {mediaItem.title}
        </h1>
      {/if}
      {#if mediaItem.speaker}
        <h3 class="text-h3 mb-4">{mediaItem.speaker}</h3>
      {/if}
      {#if mediaItem.published_at}
        <p class="text-body-small mb-4">
          <PublishDate dateTime={mediaItem.published_at} />
          {#if mediaItem._embedded?.video?.duration}
            | <Duration
              durationInMs={mediaItem._embedded?.video.duration || 0}
            />
          {/if}
          {#if mediaItem._embedded?.audio._embedded["audio-outputs"][0].duration}
            | <Duration
              durationInMs={mediaItem._embedded?.audio._embedded[
                "audio-outputs"
              ][0].duration || 0}
            />
          {/if}
        </p>
      {/if}
      {#if mediaItem.summary}
        <Prose class="[&_div]:wah-body-base">
          {@html mediaItem.summary}
        </Prose>
      {/if}
      {#if hasSharingButton}
        <ShareMedia title={mediaItem.title} />
      {/if}
    </div>
    {#if mediaItem.short_code}
      <div
        class="row-start-1 col-start-1 col-span-2 md:col-span-1 md:col-start-2 md:row-start-auto"
      >
        <div class="sap-embed-player">
          <iframe
            src={`https://subsplash.com/+f83c/embed/mi/+${mediaItem.short_code}?audio&video&info&logoWatermark&shareable&embeddable`}
            frameborder="0"
            webkitallowfullscreen
            mozallowfullscreen
            allowfullscreen
          ></iframe>
        </div>
        {#if mediaItem._embedded?.document?.title}
          <Button
            class="mt-8 mx-auto w-full"
            variant="secondary"
            href={mediaItem._embedded?.document?._links.related.href}
            text={`Download ${mediaItem._embedded?.document?.title}`}
          />
        {/if}
      </div>
    {/if}
  </div></LayeredSection
>

<style type="text/css">
  div.sap-embed-player {
    position: relative;
    width: 100%;
    height: 0;
    padding-top: 56.25%;
  }
  div.sap-embed-player > iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
</style>
